import * as React from 'react';
import { ExpandableCard } from "./ExpandableCard";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Rating from '@mui/material/Rating';
import Badge from '@mui/material/Badge';
import ManIcon from '@mui/icons-material/Man';
import Divider from '@mui/material/Divider';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useReservation } from "./ReservationContext";
import { roundToNearestTenCents } from './utils';

const StyledRating = styled(Rating)({
  '& .MuiRating-iconFilled': {
    color: '#82cad2',
  },
  '& .MuiRating-iconHover': {
    color: '#ff3d47',
  },
});

const RatingComponent = ({ label, maxVal, value, onChange, type }) => {
  return (
    <>
      <Divider sx={{ borderBottomWidth: 2, marginTop: 1 }} color="black" />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        mt={1}
        mb={1}
      >
        {!!((maxVal != 0) || value) &&
          <>
            <Box display="flex" alignItems="center" justifyContent="center" gap={0}>
              <IconButton
                variant="text"
                color="primary"
                onClick={() => onChange(value - 1)}
                disabled={value <= 0}
                sx={{ justifyContent: "end", margin: "0px", padding: "0px" }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
              <StyledRating
                name="customized-color"
                value={value}
                size="large"
                max={value + maxVal}
                icon={type === "adult" ? <ManIcon fontSize="inherit" /> : <ChildCareIcon fontSize="inherit" />}
                emptyIcon={type === "adult" ? <ManIcon fontSize="inherit" /> : <ChildCareIcon fontSize="inherit" />}
                readOnly
              />
              <IconButton
                variant="text"
                color="primary"
                onClick={() => onChange(value + 1)}
                disabled={value >= (value + maxVal)}
                sx={{ justifyContent: "start", margin: "0px", padding: "0px" }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" align="center">
              {label} hinzufügen/entfernen ({maxVal} noch zu wählen)
            </Typography>
          </>
        }
        {(maxVal == 0 && value == 0) &&
          <Typography variant="body2" align="center">
            Alle {label} zugewiesen
          </Typography>
        }
      </Box>
    </>
  );
};

export const MenuCard = ({ slide }) => {
  const { reservationState, setReservationState } = useReservation();
  const { adults, children, slides } = reservationState;

  const totalAssignedAdults = () => { return Object.values(slides).reduce((sum, slide) => sum + (slide.adults || 0), 0) };
  const totalAssignedChildren = () => { return Object.values(slides).reduce((sum, slide) => sum + (slide.children || 0), 0) };

  const handleRatingChange = (type, value) => {
    const updatedSlides = slides.map((s) => {
      if (s.id === slide.id) {
        return {
          ...s,
          [type]: value,
        };
      }
      return s;
    });
    setReservationState((prevState) => ({
      ...prevState,
      slides: updatedSlides,
    }));
  };

  let badgeData = [0, "primary"];
  if (slide.specialoffer) badgeData = ["Empfehlung", "baerendark"];
  if (slide.vegetarian) badgeData = ["Vegetarisch", "baerengruen"];

  return (
    <Paper elevation={6} sx={{ margin: "15px", marginBottom: "45px", width: "70%", marginRight: "auto", marginLeft: "auto" }}>
      <Badge sx={{ "& .MuiBadge-badge": { fontSize: 15 } }} badgeContent={badgeData[0]} color={badgeData[1]} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <ExpandableCard>
              <Typography variant="h5" sx={{ color: 'text.primary', marginBottom: '10px', fontFamily: "'Alex Brush', cursive" }}>
                {slide.title}
                {slide.price &&
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <div>
                      <span style={{ textDecoration: 'line-through', color: '#9d4a69', marginRight: '10px' }}>
                        {roundToNearestTenCents(Number(slide.price)/0.9)}€
                      </span>
                      <span style={{ fontWeight: 'bold', color: '#324b4e' }}>
                        {roundToNearestTenCents(Number(slide.price))}€ (-10%)
                      </span>
                      <div>Preis pro Person</div>
                    </div>
                  </Typography>
                }
              </Typography>

              {slide.courses.map((course, index) => (
                <React.Fragment key={index}>
                  {course.coursetitle && (
                    <Typography variant="h6" sx={{ fontFamily: "'Alex Brush', cursive" }} component="div">
                      {course.coursetitle}
                    </Typography>
                  )}
                  {course.coursesubtitle && (
                    <Typography variant="body2" sx={{ color: 'text.primary', mb: 1.0 }} component="div">
                      {course.coursesubtitle}
                    </Typography>
                  )}
                  {course.coursedesc && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                      {course.coursedesc}
                    </Typography>
                  )}
                  {index < slide.courses.length - 1 && (
                    <Divider sx={{ borderBottomWidth: 1, margin: 1 }} />
                  )}
                </React.Fragment>
              ))}
            </ExpandableCard>

            <Typography align='center'>
              <RatingComponent
                label="Erwachsene"
                maxVal={adults - totalAssignedAdults()}
                value={slide.adults || 0}
                onChange={(value) => handleRatingChange("adults", value)}
                type="adult"
              />
              {children > 0 && (
                <RatingComponent
                  label="Kinder"
                  maxVal={children - totalAssignedChildren()}
                  value={slide.children || 0}
                  onChange={(value) => handleRatingChange("children", value)}
                  type="children"
                />
              )}
            </Typography>
          </CardContent>
        </Card>
      </Badge>
    </Paper>
  );
};
